/**
 * Create/edit a Device Posture Profile (V31).
 *
 * A profile is a named requirement set that policies reference by name — the
 * DEVICE-side peer of an application group. Name + description + the
 * catalog-driven requirements editor. The parent keys the modal per target, so
 * the form is seeded at mount (no reset-in-effect, which the repo's
 * React-Compiler lint bans).
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import PostureRequirementsEditor from './PostureRequirementsEditor';
import PostureWarnings from './PostureWarnings';
import { fromPostureRows, nextPostureRow, toPostureRows, type PostureRow } from '../../utils/postureRows';
import { zodResolver } from '../../utils/zodResolver';
import type { PostureProfile, PostureProfileData } from '../../services/access.service';

const profileSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(128)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9 _.&-]*$/, 'Use letters, digits, spaces, and _.&- only'),
  description: z.string().max(512).optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

interface PostureProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PostureProfileData) => Promise<void>;
  /** When set, edit this profile in place; otherwise create. */
  profile?: PostureProfile | null;
}

const PostureProfileModal = ({ isOpen, onClose, onSave, profile = null }: PostureProfileModalProps) => {
  const editing = !!profile;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver<ProfileFormValues>(profileSchema),
    defaultValues: { name: profile?.name ?? '', description: profile?.description ?? '' },
  });

  // A new profile starts with one row so the editor is never an empty surprise.
  const [rows, setRows] = useState<PostureRow[]>(() =>
    profile ? toPostureRows(profile.requirements) : [nextPostureRow([])],
  );
  const [rowsError, setRowsError] = useState<string | null>(null);

  const handleClose = () => {
    setRows([]);
    setRowsError(null);
    onClose();
  };

  const submit = handleSubmit(async (values) => {
    const requirements = fromPostureRows(rows);
    if (Object.keys(requirements).length === 0) {
      setRowsError('Add at least one requirement with a value.');
      return;
    }
    setRowsError(null);
    await onSave({
      name: values.name,
      description: values.description || undefined,
      requirements,
    });
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editing ? 'Edit Posture Profile' : 'New Posture Profile'}
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Profile'}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <AccessFormField label="Name" htmlFor="pp-name" required error={errors.name?.message}>
          <input id="pp-name" className="input-base w-full" placeholder="Encrypted & locked" {...register('name')} />
        </AccessFormField>

        <AccessFormField label="Description" htmlFor="pp-desc" error={errors.description?.message}>
          <input id="pp-desc" className="input-base w-full" placeholder="Disk encrypted and screen lock on" {...register('description')} />
        </AccessFormField>

        <div className="pt-2 border-t border-gray-200">
          <PostureRequirementsEditor rows={rows} onChange={setRows} idPrefix="pp" />
          {rowsError && <p className="mt-1 text-xs text-danger-600">{rowsError}</p>}
          <PostureWarnings requirements={fromPostureRows(rows)} listItems={false} />
          <p className="mt-2 text-xs text-gray-500">
            All requirements must hold (AND). Matched against the device's signed posture report —{' '}
            <span className="font-medium">unknown counts as not met</span>: a device that cannot measure a fact
            reports unknown, which never equals pass.
            {editing && profile.ruleCount > 0 && (
              <>
                {' '}
                Used by {profile.ruleCount} polic{profile.ruleCount === 1 ? 'y' : 'ies'} — recompile after saving to
                push the change to the fleet.
              </>
            )}
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default PostureProfileModal;
